<template>
  <div class="settings-plugin">
    <sticky-header :title="$t('settingsPlugin.title')">
      <btn
        type="success"
        slot="ui"
        @click.native="save">
        {{ $t('common.ui.save') }}
      </btn>

      <btn
        url="/settings"
        slot="ui">
        {{ $t('common.ui.back') }}
      </btn>
    </sticky-header>

    <block
      :title="$t('settingsPlugin.configurationTitle')"
      :isLoading="!configLoaded"
      :isSaving="saveInProgress">
      <field
        v-if="this.$store.state.config.permissions.cart"
        key="field-1"
        slot="content">
        <label slot="label">
          {{ $t('settingsPlugin.enableCart') }}
        </label>

        <switcher
          slot="field"
          key="cart-enabled-switcher"
          v-model="cartEnabled" />
      </field>

      <field
        slot="content"
        key="field-2"
        v-if="cartEnabled && this.$store.state.config.permissions.cart">
        <label slot="label">
          {{ $t('settingsPlugin.resetTermAfterAddToCart') }}
        </label>

        <select
          slot="field"
          v-model="addToCartReset">
          <option :value="'datetime'">{{ $t('settingsPlugin.resetDateAndTime') }}</option>
          <option :value="'time'">{{ $t('settingsPlugin.resetOnlyTime') }}</option>
          <option :value="'none'">{{ $t('settingsPlugin.leftDateAndTime') }}</option>
        </select>
      </field>

      <field
        slot="content"
        key="field-3">
        <label slot="label">
          {{ $t('settingsPlugin.showPriceBeforeAddToCart') }}
        </label>

        <switcher
          slot="field"
          key="show-temporary-price-switcher"
          v-model="showTemporaryPrice" />

        <small
          slot="note"
          class="note">
          {{ $t('settingsPlugin.showPriceBeforeAddToCartDesc') }}
        </small>
      </field>

      <field
        v-if="servicesEnabled"
        slot="content"
        key="field-4">
        <label slot="label">
          {{ $t('settingsPlugin.twoStepServiceChoice') }}
        </label>

        <switcher
          slot="field"
          key="service-two-step-choice-switcher"
          v-model="serviceTwoStepChoice" />
        <small
          slot="note"
          class="note">
          {{ $t('settingsPlugin.byEnablingThisOptionYouWillSplitServiceChoiceToTwoSteps') }}
        </small>
      </field>

      <field
        slot="content"
        key="field-5-0">
        <label slot="label">
          {{ $t('settingsPlugin.selectFirstService') }}
        </label>

        <switcher
          slot="field"
          key="select-first-service-switcher"
          v-model="selectFirstService" />
      </field>

      <field
        slot="content"
        key="field-5">
        <label slot="label">
          {{ $t('settingsPlugin.useStyledDropdowns') }}
        </label>

        <switcher
          slot="field"
          key="use-styled-dropdowns-switcher"
          v-model="useStyledDropdowns" />
      </field>

      <field
        v-if="workersEnabled && workersVisible"
        slot="content"
        key="field-6a">
        <label slot="label">
          {{ $t('settingsPlugin.disableDefaultWorker') }}
        </label>

        <switcher
          slot="field"
          key="disable-default-worker-switcher"
          v-model="disableDefaultWorker" />
      </field>

      <field
        v-if="workersEnabled && workersVisible"
        slot="content"
        key="field-6b">
        <label slot="label">
          {{ $t('settingsPlugin.showWorkersDetails') }}
        </label>

        <switcher
          slot="field"
          key="show-workers-details-switcher"
          v-model="showWorkersDetails" />
      </field>

      <field
        slot="content"
        key="field-7"
        v-if="paymentsEnabled">
        <label slot="label">
          {{ $t('settingsPlugin.showPrices') }}
        </label>

        <select
          slot="field"
          v-model="showPrices">
          <option :value="0">{{ $t('settingsPlugin.no') }}</option>
          <option :value="1" v-if="servicesEnabled">{{ $t('settingsPlugin.showPricesOnServicesList') }}</option>
          <option :value="2">{{ $t('settingsPlugin.showPricesInSummary') }}</option>
          <option :value="3" v-if="servicesEnabled">{{ $t('settingsPlugin.showPricesOnServicesListAndSummary') }}</option>
        </select>
      </field>

      <field
        slot="content"
        key="field-8">
        <label slot="label">
          {{ $t('settingsPlugin.showAvailableHours') }}
        </label>

        <select
          slot="field"
          v-model="onlyFirstHour">
          <option :value="0">{{ $t('settingsPlugin.showAllHours') }}</option>
          <option :value="1">{{ $t('settingsPlugin.showOnlyFirstAvailableHour') }}</option>
        </select>
      </field>

      <field
        slot="content"
        key="field-9"
        v-if="onlyFirstHour">
        <label slot="label">
          {{ $t('settingsPlugin.hideHourChoice') }}
        </label>

        <switcher
          slot="field"
          key="hide-hours-on-widget-switcher"
          v-model="hideHoursOnWidget" />

        <small
          slot="note"
          class="note">
          <strong>{{ $t('settingsPlugin.warning') }}</strong> {{ $t('settingsPlugin.functionAvailableOnlyInMonthlyBoxMode') }}
        </small>
      </field>

      <field
        v-if="$store.state.config.company.rule_behavior === ''"
        slot="content"
        key="field-10">
        <label slot="label">
          {{ $t('settingsPlugin.messageAfterSucessfulSendingForm') }}
        </label>

        <textarea
          v-model="msgSent[currentLanguages.msg_sent]"
          slot="field"></textarea>

        <language-switcher
          slot="field"
          relatedTo="msg_sent" />
      </field>

      <field
        slot="content"
        key="field-11">
        <label slot="label" v-pure-html="$t('settingsPlugin.messageAfterReservationAccept')"></label>

        <textarea
          v-model="msgConfirmed[currentLanguages.msg_confirmed]"
          slot="field"></textarea>

        <language-switcher
          slot="field"
          relatedTo="msg_confirmed" />
      </field>

      <field
        slot="content"
        key="field-12">
        <label slot="label" v-pure-html="$t('settingsPlugin.messageAfterReservationReject')"></label>

        <textarea
          v-model="msgDenied[currentLanguages.msg_denied]"
          slot="field"></textarea>

        <language-switcher
          slot="field"
          relatedTo="msg_denied" />
      </field>

      <field
        slot="content"
        key="field-13">
        <label slot="label" v-pure-html="$t('settingsPlugin.messageForReservationWaitingForAccept')"></label>

        <textarea
          v-model="msgPayment[currentLanguages.msg_payment]"
          slot="field"></textarea>

        <language-switcher
          slot="field"
          relatedTo="msg_payment" />
      </field>

      <field
        v-if="waitingListEnabled"
        slot="content"
        key="field-14">
        <label slot="label" v-pure-html="$t('settingsPlugin.messageWaitingList')"></label>

        <textarea
          v-model="msgWaitingList[currentLanguages.msg_waiting_list]"
          slot="field"></textarea>

        <language-switcher
          slot="field"
          relatedTo="msg_waiting_list" />
      </field>
    </block>

    <block
      :title="$t('settingsPlugin.configureBlockingTitle')"
      :isLoading="!configLoaded"
      :isSaving="saveInProgress">
      <field slot="content">
        <label slot="label">
          {{ $t('settingsPlugin.blockReservationsFromPlugin') }}
        </label>

        <switcher
          slot="field"
          key="block-widget"
          v-model="blockWidget" />
      </field>

      <field slot="content">
        <label slot="label">
          <template v-if="blockWidget">{{ $t('settingsPlugin.messageWillAppearInsteadOfForm') }}</template>
          <template v-else>{{ $t('settingsPlugin.messageWillAppearOverForm') }}</template>
        </label>

        <textarea
          v-model="blockWidgetText[currentLanguages.block_widget_text]"
          slot="field"></textarea>

        <language-switcher
          slot="field"
          relatedTo="block_widget_text" />
      </field>

      <field slot="content">
        <label slot="label">
          {{ $t('settingsPlugin.messageStyle') }}
        </label>

        <select
          slot="field"
          v-model="blockWidgetType">
          <option value="info">{{ $t('settingsPlugin.messageInfo') }}</option>
          <option value="warning">{{ $t('settingsPlugin.messageWarning') }}</option>
          <option value="danger">{{ $t('settingsPlugin.messageDanger') }}</option>
        </select>
      </field>
    </block>

    <block
      :title="$t('settingsPlugin.configureTrackingTitle')"
      :isLoading="!configLoaded"
      :isSaving="saveInProgress">
      <field slot="content">
        <label slot="label">
          {{ $t('settingsPlugin.useGA') }}
        </label>

        <switcher
          slot="field"
          key="block-widget"
          v-model="useGA" />
      </field>

      <field slot="content">
        <label slot="label">
          {{ $t('settingsPlugin.useFB') }}
        </label>

        <switcher
          slot="field"
          key="block-widget"
          v-model="useFB" />
      </field>
    </block>
  </div>
</template>

<script>
import Block from './../common/Block';
import Configuration from './../mixins/Configuration';
import ErrorUtils from './../helpers/ErrorUtils.js';
import Languages from './../mixins/Languages';
import SettingsBasicBlock from './../ui/SettingsBasicBlock';
import StickyHeader from './../common/StickyHeader';

export default {
  name: 'settings-plugin',
  mixins: [
    Configuration,
    Languages
  ],
  components: {
    'block': Block,
    'settings-basic-block': SettingsBasicBlock,
    'sticky-header': StickyHeader
  },
  data () {
    return {
      errors: [],
      showPrices: 0,
      onlyFirstHour: 0,
      hideHoursOnWidget: 0,
      msgSent: this.$t('settingsPlugin.msgSent'),
      msgConfirmed: this.$t('settingsPlugin.msgConfirmed'),
      msgDenied: this.$t('settingsPlugin.msgDenied'),
      msgWaitingList: this.$t('settingsPlugin.msgWaitingList'),
      msgPayment: '',
      translations: {},
      blockWidget: 0,
      blockWidgetType: 'info',
      blockWidgetText: '',
      currentLanguages: {
        msg_sent: '',
        msg_confirmed: '',
        msg_denied: '',
        msg_payment: '',
        msg_waiting_list: '',
        block_widget_text: ''
      },
      configLoaded: false,
      saveInProgress: false,
      addToCartReset: 'datetime',
      cartEnabled: false,
      disableDefaultWorker: false,
      showTemporaryPrice: false,
      serviceTwoStepChoice: false,
      selectFirstService: true,
      useStyledDropdowns: false,
      showWorkersDetails: false,
      useGA: false,
      useFB: false
    };
  },
  mounted () {
    if (this.$store.state.config.user.id !== 0) {
      this.configurationLoad();
    }

    this.$bus.$on('configuration-loaded', this.configurationLoad);
  },
  methods: {
    configurationLoad () {
      this.configureMultilanguage();
      this.loadTranslations();
    },
    configureMultilanguage () {
      this.currentLanguages.msg_sent = this.defaultLanguageCode;
      this.currentLanguages.msg_confirmed = this.defaultLanguageCode;
      this.currentLanguages.msg_denied = this.defaultLanguageCode;
      this.currentLanguages.msg_payment = this.defaultLanguageCode;
      this.currentLanguages.msg_waiting_list = this.defaultLanguageCode;
      this.currentLanguages.block_widget_text = this.defaultLanguageCode;
      this.$bus.$on('change-field-language', this.changeCurrentLanguageForField);
    },
    loadTranslations () {
      this.$http.post('/panel/general/get-configuration-translations').then(response => {
        this.translations = response.data;

        if (!Object.keys(this.translations).length) {
          this.translations = this.createEmptyTranslationsObject([
            'msg_sent',
            'msg_confirmed',
            'msg_denied',
            'msg_payment',
            'msg_waiting_list',
            'block_widget_text'
          ]);
        }

        this.setFormData();
      }).catch(error => {
        ErrorUtils.handleLoadError(error, '/panel/general/get-configuration-translations', this.$router, this.$bus);
      });
    },
    setFormData () {
      this.configLoaded = true;
      this.onlyFirstHour = this.$store.state.config.company.only_first_hour;
      this.hideHoursOnWidget = this.$store.state.config.company.hide_hours_on_widget;
      this.showPrices = this.$store.state.config.company.show_prices;

      this.msgSent = this.getLanguageFieldValues(
        this.translations,
        'msg_sent',
        this.$store.state.config.company.msg_sent
      );

      this.msgConfirmed = this.getLanguageFieldValues(
        this.translations,
        'msg_confirmed',
        this.$store.state.config.company.msg_confirmed
      );

      this.msgDenied = this.getLanguageFieldValues(
        this.translations,
        'msg_denied',
        this.$store.state.config.company.msg_denied
      );

      this.msgPayment = this.getLanguageFieldValues(
        this.translations,
        'msg_payment',
        this.$store.state.config.company.msg_payment
      );

      this.msgWaitingList = this.getLanguageFieldValues(
        this.translations,
        'msg_waiting_list',
        this.$store.state.config.company.msg_waiting_list
      );

      this.blockWidgetText = this.getLanguageFieldValues(
        this.translations,
        'block_widget_text',
        this.$store.state.config.company.block_widget_text
      );

      if (!this.servicesEnabled && (this.showPrices === 1 || this.showPrices === 3)) {
        this.showPrices = 0;
      }

      this.addToCartReset = this.$store.state.config.company.add_to_cart_reset;
      this.cartEnabled = this.$store.state.config.company.cart_enabled;
      this.disableDefaultWorker = this.$store.state.config.company.disable_default_worker;
      this.serviceTwoStepChoice = this.$store.state.config.company.service_two_step_choice;
      this.selectFirstService = this.$store.state.config.company.select_first_service;
      this.useStyledDropdowns = this.$store.state.config.company.use_styled_dropdowns;
      this.showWorkersDetails = this.$store.state.config.company.show_workers_details;
      this.showTemporaryPrice = this.$store.state.config.company.show_temporary_price;
      this.blockWidget = this.$store.state.config.company.block_widget;
      this.blockWidgetType = this.$store.state.config.company.block_widget_type;
      this.useGA = !!this.$store.state.config.company.use_ga;
      this.useFB = !!this.$store.state.config.company.use_fb;
    },
    validate () {

    },
    save () {
      this.validate();
      this.saveInProgress = true;

      if (this.errors.length) {
        this.$bus.$emit('notification-display', {
          message: this.$t('common.ui.invalidFields'),
          type: 'error'
        });

        return;
      }

      this.saveForm();
    },
    saveForm () {
      let formData = this.prepareFormData();
      formData.settings = 'plugin';

      this.$http.post('/panel/settings/settings-save', formData).then(response => {
        if (response.data.success) {
          this.$bus.$emit('notification-display', {
            message: this.$t('settingsPlugin.settingsSaved'),
            type: 'success'
          });

          this.$store.commit('config/setCompanyPartialConfig', this.prepareFormData());
          this.$router.push('/settings');
          this.saveInProgress = false;
        }
      }).catch(error => {
        ErrorUtils.handleSubmitError(error, '/panel/settings/settings-save', this.$router, this.$bus, this.saveForm.bind(this));
        this.saveInProgress = false;
      });
    },
    prepareFormData () {
      let formData = {
        show_prices: this.showPrices,
        only_first_hour: this.onlyFirstHour,
        msg_sent: this.msgSent[this.defaultLanguageCode],
        msg_confirmed: this.msgConfirmed[this.defaultLanguageCode],
        msg_denied: this.msgDenied[this.defaultLanguageCode],
        msg_payment: this.msgPayment[this.defaultLanguageCode],
        msg_waiting_list: this.msgWaitingList[this.defaultLanguageCode]
      };

      formData['add_to_cart_reset'] = this.addToCartReset;
      formData['cart_enabled'] = this.cartEnabled;
      formData['disable_default_worker'] = this.disableDefaultWorker;
      formData['service_two_step_choice'] = this.serviceTwoStepChoice;
      formData['select_first_service'] = this.selectFirstService;
      formData['use_styled_dropdowns'] = this.useStyledDropdowns;
      formData['show_workers_details'] = this.showWorkersDetails;
      formData['show_temporary_price'] = this.showTemporaryPrice;
      formData['hide_hours_on_widget'] = this.hideHoursOnWidget;
      formData['block_widget'] = this.blockWidget;
      formData['block_widget_type'] = this.blockWidgetType;
      formData['block_widget_text'] = this.blockWidgetText[this.defaultLanguageCode];
      formData['use_ga'] = this.useGA;
      formData['use_fb'] = this.useFB;

      if (this.languagesAreAvailable) {
        formData.translations = this.prepareTranslationsObject({
          msg_sent: this.msgSent,
          msg_confirmed: this.msgConfirmed,
          msg_denied: this.msgDenied,
          msg_payment: this.msgPayment,
          msg_waiting_list: this.msgWaitingList,
          block_widget_text: this.blockWidgetText
        });
      }

      return formData;
    }
  },
  beforeDestroy () {
    this.$bus.$off('change-field-language', this.changeCurrentLanguageForField);
    this.$bus.$off('configuration-loaded', this.setFormData);
  }
};
</script>

<style lang="scss" scoped>
@import '../../assets/scss/variables.scss';

.settings-plugin {
  padding: 0 0 2rem 0;
  width: 100%;
}

@media (max-width: 1100px) {
  .settings-plugin {
    .field {
      input,
      select,
      textarea {
        width: calc(66% - 50px);
      }
    }
  }
}

@media (max-width: 640px) {
  .settings-plugin {
    margin-top: 6rem;
  }
}

@media (max-width: 580px) {
  .settings-plugin {
    .field {
      label,
      input,
      select,
      textarea {
        text-align: left;
        width: calc(100% - 50px);
      }

      label {
        margin-bottom: .5rem;
      }

      small.note {
        padding: .5rem 0 0 0;
      }
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/views/SettingsPlugin.vue